import type { MediaFolder } from '../api/folders.api';

/** A folder with the depth it should be drawn at. */
export interface FolderRow {
  folder: MediaFolder;
  depth: number;
  /** Whether the rail should draw a chevron for this row at all. */
  hasChildren: boolean;
}

/**
 * The flat list the server sends, ordered as a tree and given a depth per row.
 *
 * A rail draws rows, not nesting, so the shape it wants is a list with an indent rather than a graph
 * it has to walk while rendering. Doing it here keeps the recursion in one tested place instead of
 * inside a component.
 *
 * A folder whose parent is missing is treated as a root rather than dropped. The parent may simply be
 * outside what the caller can see, and a folder that vanishes because of that is worse than one drawn
 * a level too high.
 *
 * Depth is capped when recursing so a cycle in the data cannot hang the render. Cycles should not
 * happen, but a render loop is not the place to find out that one did.
 */
const MAX_DEPTH = 8;

export function toFolderRows(folders: readonly MediaFolder[]): FolderRow[] {
  const children = new Map<string | null, MediaFolder[]>();
  const ids = new Set(folders.map((folder) => folder.id));

  for (const folder of folders) {
    const parent = folder.parentId !== null && ids.has(folder.parentId) ? folder.parentId : null;
    const siblings = children.get(parent) ?? [];
    siblings.push(folder);
    children.set(parent, siblings);
  }

  const rows: FolderRow[] = [];
  const walk = (parentId: string | null, depth: number): void => {
    if (depth > MAX_DEPTH) {
      return;
    }
    for (const folder of children.get(parentId) ?? []) {
      const hasChildren = (children.get(folder.id) ?? []).length > 0;
      rows.push({ folder, depth, hasChildren });
      walk(folder.id, depth + 1);
    }
  };

  walk(null, 0);
  return rows;
}

/**
 * The chain from the root down to one folder, that folder last.
 *
 * A header naming where you are standing needs the ancestors above it, which the flat list only
 * implies. Walking upwards from the folder rather than downwards from the roots means the cost is
 * the depth of one branch rather than the size of the tree.
 *
 * The walk stops at a parent that is not in the list, the same way `toFolderRows` treats such a
 * folder as a root: the parent may be outside what the caller can see, and a path that reads one
 * level too shallow beats no path at all. Bounded by the same `MAX_DEPTH`, so a cycle cannot hang a
 * render here either.
 */
export function folderPath(
  folders: readonly MediaFolder[],
  folderId: string | undefined,
): MediaFolder[] {
  if (folderId === undefined) {
    return [];
  }

  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const path: MediaFolder[] = [];

  let current = byId.get(folderId);
  while (current !== undefined && path.length <= MAX_DEPTH) {
    path.unshift(current);
    current = current.parentId !== null ? byId.get(current.parentId) : undefined;
  }

  return path;
}

/**
 * Which rows a rail should draw once some folders are collapsed.
 *
 * `toFolderRows` returns a pre-order walk, so a collapsed folder's descendants are exactly the run of
 * rows immediately following it whose depth is greater than its own. That makes hiding them a matter
 * of comparing depth rather than walking the tree a second time. Only a row that is actually drawn
 * gets to hide what follows it, so a folder inside an already-hidden branch cannot reopen it by being
 * expanded itself; it simply stays invisible until an ancestor is expanded too.
 */
export function visibleFolderRows(
  rows: readonly FolderRow[],
  collapsedIds: ReadonlySet<string>,
): FolderRow[] {
  const visible: FolderRow[] = [];
  let hiddenBelowDepth: number | null = null;

  for (const row of rows) {
    if (hiddenBelowDepth !== null && row.depth > hiddenBelowDepth) {
      continue;
    }
    hiddenBelowDepth = null;
    visible.push(row);
    if (row.hasChildren && collapsedIds.has(row.folder.id)) {
      hiddenBelowDepth = row.depth;
    }
  }

  return visible;
}

/**
 * The rows a search leaves, each still under the folders it lives in.
 *
 * A hit alone would be a name with no path, and two folders called `Drafts` in different branches
 * would be indistinguishable. So a match keeps its ancestors, which is also what lets the indent go
 * on meaning what it means. A branch nothing matched is dropped whole.
 *
 * Descendants of a hit are not kept unless they matched too: a search asks which folders answer to a
 * word, and pulling a whole subtree in behind one of them answers a different question. `hasChildren`
 * is recomputed from what survived rather than carried over, so no row is left with a chevron that
 * would open onto nothing.
 *
 * The ancestors are read off the pre-order walk rather than the tree: in that order a row's ancestor
 * chain is the last row seen at each depth above it.
 */
export function matchingFolderRows(rows: readonly FolderRow[], search: string): FolderRow[] {
  const term = search.trim().toLowerCase();
  if (term.length === 0) {
    return [...rows];
  }

  const kept: FolderRow[] = [];
  const keptIds = new Set<string>();
  const chain: FolderRow[] = [];

  for (const row of rows) {
    chain.length = row.depth;
    chain[row.depth] = row;

    if (!row.folder.name.toLowerCase().includes(term)) {
      continue;
    }

    for (const ancestor of chain) {
      if (!keptIds.has(ancestor.folder.id)) {
        keptIds.add(ancestor.folder.id);
        kept.push(ancestor);
      }
    }
  }

  return kept.map((row, index) => ({
    ...row,
    hasChildren: (kept[index + 1]?.depth ?? row.depth) > row.depth,
  }));
}
